/**
 * Error Record Modal
 * Detailed view of a single error record with field highlighting,
 * value formatting, and contextual severity information.
 */

import React from 'react';
import { X, Info, Tag, AlertTriangle, Minus } from 'lucide-react';
import { getSeverityConfig } from './severityConfig';
import { SeverityBadge, SeverityIcon } from './ErrorCard';

/* ─── Field importance detection ─────────────── */
const getFieldImportance = (key, value) => {
  const k = key.toLowerCase();
  if (k.includes('ticket') && k.includes('no')) return 'primary';
  if (k.includes('duplicate') || k.includes('count')) return 'warning';
  if (k.includes('error') || k.includes('issue')) return 'error';
  if (value === null || value === '' || value === undefined) return 'missing';
  return 'normal';
};

const importanceStyles = {
  primary: {
    wrapper: 'border-primary-200 bg-primary-50/50',
    label: 'text-primary-700',
    tag: 'Key Field',
    tagClass: 'bg-primary-100 text-primary-700',
  },
  warning: {
    wrapper: 'border-amber-200 bg-amber-50/50',
    label: 'text-amber-700',
    tag: 'Issue',
    tagClass: 'bg-amber-100 text-amber-700',
  },
  error: {
    wrapper: 'border-red-200 bg-red-50/50',
    label: 'text-red-700',
    tag: 'Error',
    tagClass: 'bg-red-100 text-red-700',
  },
  missing: {
    wrapper: 'border-slate-200 bg-slate-50',
    label: 'text-slate-500',
    tag: 'Missing',
    tagClass: 'bg-slate-200 text-slate-600',
  },
  normal: {
    wrapper: 'border-slate-200 bg-surface',
    label: 'text-slate-600',
    tag: null,
    tagClass: '',
  },
};

/* ─── Value formatter ────────────────────────── */
const formatValue = (key, value) => {
  if (value === null || value === undefined) {
    return <span className="text-slate-400 italic text-sm">null</span>;
  }
  if (value === '') {
    return <span className="text-slate-400 italic text-sm">empty</span>;
  }

  // Dates
  if (
    key.toLowerCase().includes('date') ||
    key.toLowerCase().includes('time') ||
    key.toLowerCase().includes('timestamp')
  ) {
    try {
      const date = new Date(value);
      if (!isNaN(date.getTime())) {
        return (
          <div>
            <span className="text-sm font-semibold text-slate-900">
              {date.toLocaleDateString()}
            </span>
            <span className="text-xs text-slate-500 ml-1.5">{date.toLocaleTimeString()}</span>
          </div>
        );
      }
    } catch (e) {
      /* fall through */
    }
  }

  // Numbers
  if (typeof value === 'number' || (!isNaN(value) && !isNaN(parseFloat(value)))) {
    const num = parseFloat(value);
    return (
      <span
        className="text-sm font-semibold text-slate-900 tabular-nums"
        style={{ fontFamily: 'IBM Plex Mono, monospace' }}
      >
        {num > 999 ? num.toLocaleString() : num}
      </span>
    );
  }

  return <span className="text-sm font-medium text-slate-900">{String(value)}</span>;
};

/* ─── Humanize field name ────────────────────── */
const humanize = (str) =>
  str
    .replace(/([A-Z])/g, ' $1')
    .replace(/[_-]/g, ' ')
    .replace(/^\s/, '')
    .split(' ')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');

/* ─── Main Component ─────────────────────────── */
const ErrorRecordModal = ({ errorRecord, errorResult, onClose }) => {
  const severity = errorResult.severity_level || 'none';
  const config = getSeverityConfig(severity);

  return (
    <div
      className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[60] p-4"
      onClick={onClose}
    >
      <div
        className="bg-surface rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col animate-slideUp"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <div className="flex items-center gap-3">
            <div
              className={`flex items-center justify-center w-9 h-9 rounded-lg ${config.bg} ${config.border} border`}
            >
              <SeverityIcon severity={severity} />
            </div>
            <div>
              <h2
                className="text-lg font-bold text-slate-900"
                style={{ fontFamily: 'DM Sans, sans-serif' }}
              >
                Record Details
              </h2>
              <p className="text-xs text-slate-500">{errorResult.rule_name}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
          {/* Rule context */}
          <div className="rounded-xl border border-primary-200 bg-primary-50/50 p-3.5">
            <div className="flex items-center gap-2 mb-2">
              <Info className="w-3.5 h-3.5 text-primary-600" />
              <span className="text-[10px] font-semibold text-primary-700 uppercase tracking-widest">
                Rule Context
              </span>
            </div>
            <div className="grid grid-cols-3 gap-3 text-xs">
              <div>
                <span className="text-primary-500 font-medium">Rule</span>
                <p className="text-primary-800 font-semibold mt-0.5">{errorResult.rule_name}</p>
              </div>
              <div>
                <span className="text-primary-500 font-medium">Severity</span>
                <div className="mt-0.5">
                  <SeverityBadge severity={severity} />
                </div>
              </div>
              <div>
                <span className="text-primary-500 font-medium">Total Errors</span>
                <p
                  className="text-primary-800 font-bold mt-0.5"
                  style={{ fontFamily: 'IBM Plex Mono, monospace' }}
                >
                  {errorResult.error_count || 0}
                </p>
              </div>
            </div>
          </div>

          {/* Record fields */}
          <div>
            <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-widest mb-3">
              Field Values
            </p>
            <div className="space-y-2">
              {Object.entries(errorRecord).map(([key, value]) => {
                const importance = getFieldImportance(key, value);
                const style = importanceStyles[importance];

                return (
                  <div
                    key={key}
                    className={`rounded-lg border p-3.5 transition-all ${style.wrapper}`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span
                        className={`text-[10px] font-semibold uppercase tracking-wider ${style.label}`}
                      >
                        {humanize(key)}
                      </span>
                      {style.tag && (
                        <span
                          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${style.tagClass}`}
                        >
                          {importance === 'primary' && <Tag className="w-2.5 h-2.5" />}
                          {importance === 'warning' && <AlertTriangle className="w-2.5 h-2.5" />}
                          {importance === 'missing' && <Minus className="w-2.5 h-2.5" />}
                          {style.tag}
                        </span>
                      )}
                    </div>
                    <div>{formatValue(key, value)}</div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Context note */}
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-3.5">
            <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-widest mb-1.5">
              Guidance
            </p>
            <ul className="text-xs text-slate-600 space-y-1">
              <li className="flex items-start gap-1.5">
                <span className="w-1 h-1 rounded-full bg-slate-400 mt-1.5 shrink-0" />
                Flagged by the "{errorResult.rule_name}" detection rule
              </li>
              <li className="flex items-start gap-1.5">
                <span className="w-1 h-1 rounded-full bg-slate-400 mt-1.5 shrink-0" />
                Review highlighted fields for data quality issues
              </li>
              <li className="flex items-start gap-1.5">
                <span className="w-1 h-1 rounded-full bg-slate-400 mt-1.5 shrink-0" />
                Correct data in the amendment system or adjust the rule if false positive
              </li>
            </ul>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end px-6 py-4 border-t border-slate-100">
          <button
            onClick={onClose}
            className="px-4 py-2.5 rounded-lg text-sm font-semibold text-slate-600 hover:bg-slate-100 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ErrorRecordModal;
